import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { rentalService } from "../services/rental.service";
import type {
  RentalResponseDTO,
  RentalSearchParams,
} from "../types/rental.types";
import { RentalStatus } from "@/shared/types/enums";

export interface PickupListFilters {
  /** Formato `YYYY-MM-DD` — limite inferior de `expectedOutAt`. */
  expectedOutStartDate: string;
  /** Formato `YYYY-MM-DD` — limite superior de `expectedOutAt`. */
  expectedOutEndDate: string;
  contractNumbers?: string[];
}

interface UseRentalPickupListOptions {
  initialFilters: PickupListFilters;
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 10;

export function useRentalPickupList({
  initialFilters,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseRentalPickupListOptions) {
  const [data, setData] = useState<RentalResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<PickupListFilters>(initialFilters);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (targetPage: number) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const searchParams: RentalSearchParams = {
          ...filters,
          statuses: [RentalStatus.COMPLETE],
          page: targetPage,
          size: pageSize,
          sortBy: "expectedOutAt",
          sortDirection: "ASC",
        };
        const response = await rentalService.search(searchParams);
        if (requestIdRef.current !== requestId) return;
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setPage(targetPage);
      } catch (err: unknown) {
        if (requestIdRef.current !== requestId) return;
        const message = getApiErrorMessage(
          err,
          "Erro ao carregar aluguéis para retirada",
        );
        setError(message);
        toast.error(message);
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [filters, pageSize],
  );

  useEffect(() => {
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(0);
  }, [load]);

  const removeLocal = useCallback((id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
  }, []);

  const reload = useCallback(() => {
    load(page);
  }, [load, page]);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    totalElements,
    filters,
    setFilters,
    setPage: load,
    reload,
    removeLocal,
  };
}
